import permissions from 'scoreboard/components/permissions'
import Dataset from './model/dataset'
const { DATASET, FOLDER } = Dataset
import _ from 'lodash'

const debug = false

function log(msg) {
	if (debug) console.log(msg)
}

/**
 * Factory that returns the datasetsPasteAllowed function.  We do it this way so
 * we can capture some required information in a closure.
 *
 * NOTE: please leave log statements in place because it's not possible
 * to debug this with the debugger when using drag and drop in the browser
 *
 * @param {treeCallback} getTree returns the tree
 */
function datasetsPasteAllowedFactory(getTree) {

	/**
	 * Returns whether a paste operation is allowed for Datasets.
	 * @param node the node to be pasted
	 * @param parent the new parent for the node (null if pasting to a top-level org)
	 * @param empty true if the org is empty
	 * @param mode either 'copy' or 'move'
	 */
	function datasetsPasteAllowed(node, parent, empty, mode) {
		log('--- testing DND rules')
		if (parent) { // not moving to the top level
			return canMoveUnderParent(node, parent)
		} else { // moving to the top level
			return canMoveTopLevelDataset(node, parent)
		}
	}

	/**
	 * Returns whether we can move a node under a specific parent which is NOT
	 * one of the top level groupings
	 * @param node the node to be pasted
	 * @param parent the new parent for the node (null if pasting to a top-level org)
	 */
	function canMoveUnderParent(node, parent) {
		// when moving not under the top level
		if (parent.type === FOLDER || parent.type === DATASET) {
			log('moving under a dataset folder')
			let root = getTree().get(node.rootDatasetId)
			if (node.type === FOLDER) {
				if (parent.type === DATASET && _.isNil(node.rootDatasetId)) {
					if (!parent.modifyViews) {
						log('can only move folders under datasets if ' +
							'the user has the modify view permissions on the ' +
							'dataset')
						return false
					}
					log('if the folder isn\'t currently under a ' +
						'dataset, it\'s OK to move it under one as long as' +
						'it doesn\'t have children')
					return !hasDatasetChild(node)
				}
				log('can move folders that are still under the same ' +
					'dataset. Also OK if both aren\'t under a dataset')
				return node.rootDatasetId == parent.rootDatasetId
			} else if (node.type == DATASET) {
				if (!node.modifyViews && !node.administer) {
					log('can only move datasets if the user has the modify view permissions')
					return false
				}
				if (parent.type != FOLDER) {
					log('can only move datasets under folders')
					return false
				}
				if (!_.isNil(parent.rootDatasetId)) {
					log("can't move datasets under folders which are in turn under other datasets")
					return false
				}
				log('Can move datasets under folders which aren\'t under other datasets')
				return true
			} else { // node.type == VIEW
				log('if the user has permission, can move views under the same dataset')
				return (parent.rootDatasetId == node.rootDatasetId) &&
					root.modifyViews
			}
		} else {
			log('nothing is allowed under views')
			return false
		}
	}

	/**
	 * Returns whether a top-level dataset can be reordered with another top-level dataset
	 */
	 function canMoveTopLevelDataset(node) {
	 	if (node.type === 'view') {
	 		log('can\'t move view to top level')
			return false
		}
		if (node.type === 'folder') {
			if (_.isNil(node.rootDatasetId)) {
				if (permissions.modifyAllDatasets()) {
					log('users with "Modify All Datasets" can reorder top-level folders')
					return true
				} else {
					// whether the user can admin all datasets under
					if (isAdminAllChildren(node)) {
						log('user can admin all datasets under the folder, so it can be moved')
						return true
					} else {
						log("user can't admin one or more datasets under folder, so it can't be moved")
						return false
					}
				}
			} else {
				log('can only move empty subordinate folders to top level')
				return _.isEmpty(getTree().children(node))
			}
		}
		const isModifiable = new Dataset(node).isModifyDataset()
		if (isModifiable) {
			log('users with "Modify All Datasets" can reorder datasets')
		} else {
			log(`users lacks "Modify All Datasets" so can't reorder datasets`)
		}
		return isModifiable
	}

	function hasDatasetChild(node) {
	 	let children = getTree().children(node)
		return _.find(children, child => {
			return child.type === 'dataset' || hasDatasetChild(child)
		})
	}

	/**
	 * Returns whether the current user has permission to administer all the
	 * datasets under the given node
	 */
	function isAdminAllChildren(node) {
		let children = getTree().children(node)
		return _.every(children, child => {
			return (child.administer || child.type !== 'dataset') &&
				isAdminAllChildren(child)
		})
	}

	return datasetsPasteAllowed
}



export default datasetsPasteAllowedFactory

datasetsPasteAllowedFactory.__filename__ = 'datasets/datasets-paste-allowed-factory.js'
